<!-- @format -->
<template>
    <div class="categories-edit-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3>Select Categories</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div class="form-row">
                <label>Categories</label>
                <div class="categories">
                    <div
                        v-for="category in collateral.categories"
                        :key="category.id"
                        class="category"
                    >
                        {{ category.name }}
                        <div class="background">
                            <x-svg @click.native="removeCategory(category)" />
                        </div>
                    </div>
                </div>
                <collateral-category-dropdown
                    :clientId="collateral.clientId"
                    @input="addCategory($event)"
                />
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button :loading="steps[step].loading" @click="steps[step].action">
                    {{ steps[step].buttonText }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const CollateralCategoryDropdown = () =>
    import('@/components/collateral/collateral-category-dropdown');
const XSvg = () => import('@/components/svg/connectwise/x-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'categories-edit-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'collateral-category-dropdown': CollateralCategoryDropdown,
        'x-svg': XSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isInitializing: true,

            collateral: {
                id: null,
                name: null,
                previewImageUrl: null,
                url: null,
                categories: [],
                clientId: null,
            },

            step: null,

            steps: {
                edit: {
                    title: 'Edit Collateral',
                    buttonText: 'Save',
                    action: this.save,
                    loading: false,
                },

                saving: {
                    title: 'Saving Collateral',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },
            },
        };
    },

    created() {
        this.step = 'edit';
        this.collateral.id = this.widget.options.collateralId;

        this.getCollateral()
            .catch((error) => {
                this.showFailNotification('An error occurred while fetching your collateral.', { error });
            })
            .finally(() => {
                this.isInitializing = false;
            });
    },

    methods: {
        addCategory(newCategory) {
            var exists = this.collateral.categories.some(
                (category) => category.id === newCategory.id
            );
            if (!exists) {
                this.collateral.categories.push(newCategory);
            }
        },

        removeCategory(category) {
            this.collateral.categories.splice(this.collateral.categories.indexOf(category), 1);
        },

        getCollateral() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Collaterals?$expand=CollateralCategoryCollaterals($expand=CollateralCategory)&$filter=Id eq ${this.collateral.id}`
                    )
                    .then((response) => {
                        var responseValue = response.data.value[0];

                        this.collateral.previewImageUrl = responseValue.PreviewImageUrl;
                        this.collateral.url = responseValue.Url;
                        this.collateral.fileExtension = responseValue.FileExtension;
                        this.collateral.fileType = (responseValue.FileType || '').toLowerCase();
                        this.collateral.name = this.$helpers.getFilenameWithoutExtension(
                            responseValue.Name,
                            responseValue.FileExtension
                        );
                        this.collateral.clientId = responseValue.ClientId;
                        this.collateral.categories = this.createCategories(
                            responseValue.CollateralCategoryCollaterals
                        );

                        resolve();
                    })
                    .catch(reject);
            });
        },

        createCategories(categories) {
            return categories.map((category) => {
                return {
                    id: category.CollateralCategory.Id,
                    name: category.CollateralCategory.Name,
                };
            });
        },

        save() {
            this.step = 'saving';

            this.saveCollateral()
                .then(this.saveCategories)
                .then(() => {
                    EventBus.$emit('collateral:update');
                    this.$helpers.showSuccessNotification('Your collateral has been updated.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your collateral.',
                        { error },
                    );
                });
        },

        saveCollateral() {
            var name = this.collateral.fileExtension
                ? `${this.collateral.name}.${this.collateral.fileExtension}`
                : this.collateral.name;

            var request = {
                Name: name,
            };

            return this.$api.odataPatch(`/service/Collaterals(${this.collateral.id})`, request);
        },

        saveCategories() {
            var categoryIds = this.collateral.categories.map((category) => category.id);

            return this.$api.put(`/api/Collaterals/${this.collateral.id}/Categories`, categoryIds);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.categories-edit-widget .form-row {
    margin-bottom: 20px;
}

.categories-edit-widget .app-file-icon {
    max-width: 100px;
    margin: 0 auto;
}

.categories-edit-widget label {
    color: #382153;
    font: 500 16px/24px Roboto;
}

.categories-edit-widget .action-container {
    display: flex;
}

.categories-edit-widget .action-container .btn {
    margin-left: 15px;
}

.categories-edit-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.categories-edit-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.categories-edit-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.categories-edit-widget .collateral-category-dropdown {
    margin-bottom: 10px;
}

.categories-edit-widget .categories {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.categories-edit-widget .categories .category {
    background: #ffffff;
    border: 2px solid #cac6cb;
    font-size: 11px;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
    color: #382153;
    font: 400 12px/20px Roboto;
    padding: 0px 0px 0px 10px;
}

.categories-edit-widget .categories .background {
    margin-left: 7px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
}

.categories-edit-widget .categories .category:hover .background {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #f5d5e7;
    margin-left: 7px;
}

.categories-edit-widget .categories .category:hover {
    border: 2px solid #877d88;
}

.categories-edit-widget .categories .category svg {
    width: 8px;
    height: 8px;
    cursor: pointer;
    margin-left: 6px;
}

.categories-edit-widget .categories .category svg path {
    fill: #ee206f;
}
</style>
